import { FC, forwardRef, useMemo } from "react"
import MinusSvg from '@/shared/assets/images/Minus.svg'
import PlusSvg from '@/shared/assets/images/Plus.svg'
import { TClassOfService } from "@/shared/api/aviabooking"
import { AvailablePassengerTypes, PassengerTypeNames, AvailableClassOfService, ClassOfServiceNames } from "@/entities/aviabooking-reference"
import { useFormContext } from "react-hook-form"

interface PassengerCountSelectProps {
    count: number
    title: string
    description: string
    setCount: (count: number) => void
}

const PassengerCountSelect: FC<PassengerCountSelectProps> = ({ count, setCount, title, description }) => {
    const handleIncrease = () => {
        setCount(count + 1)
    }

    const handleDecrease = () => {
        if (count === 0) return
        setCount(count - 1)
    }

    return (
        <div className="flex justify-between gap-5 items-center">
            <div className="flex flex-col gap-1">
                <div className="text-base text-start">
                    {title}
                </div>
                <div className="text-sm text-start text-slate-400">
                    {description}
                </div>
            </div>
            <div className="flex items-center">
                <button 
                    onClick={handleDecrease} 
                    disabled={count === 0}
                    className="rounded-full flex items-center justify-center text-center overflow-hidden h-8 w-8 bg-color-primary text-white disabled:bg-slate-200 disabled:fill-slate-500"
                >
                    <MinusSvg width={20} height={20} />
                </button>
                <div
                    className="mx-3 text-bold text-lg"
                >
                    {count}
                </div>
                <button
                    onClick={handleIncrease}
                    className="rounded-full flex items-center justify-center text-center overflow-hidden h-8 w-8 bg-color-primary text-white disabled:bg-slate-200 disabled:fill-slate-500"
                >
                    <PlusSvg width={20} height={20} />
                </button>
            </div>
        </div>
    )
}

interface ClassOfServiceSelectOptionProps {
    option: TClassOfService
    title: string
    selected: TClassOfService
    setSelected: (option: TClassOfService) => void
}

const ClassOfServiceSelectOption: FC<ClassOfServiceSelectOptionProps> = ({
    option,
    title,
    selected,
    setSelected
}) => {
    const isSelected = useMemo(() => {
        return option === selected
    }, [option, selected])

    return (
        <div
            onClick={() => setSelected(option)}
            className="flex w-full hover:cursor-pointer justify-between items-center"
        >
            <div className="text-sm">
                {title}
            </div>
            <div className={`w-[20px] rounded-full overflow-hidden h-[20px] transition-all ${isSelected ? "border-color-primary border-[6px]" : "border-zinc-300 border-[2px]"}`}/>
        </div>
    )
}

export const FlightOptionsDropdownOverlay = forwardRef<HTMLDivElement>((_, ref) => {
    const methods = useFormContext()
    const passengers = methods.watch('passengers') as Record<string, { type: string, count: number }>
    const classOfService = methods.watch('classOfService') as TClassOfService

    const handlePassengerCountChange = (type: string, count: number) => {
        methods.setValue(`passengers.${type}.count`, count, { shouldValidate: true })
        methods.setValue('passengersCount', Object.values(passengers).reduce((acc, curr) => acc + curr.count, 0), { shouldValidate: true })
    }

    return (
        <div ref={ref} className="flex bg-white shadow-sm flex-col rounded-xl border overflow-hidden" >
            <div className="p-5 flex flex-col">
                <div className="mb-4 font-semibold text-lg">
                    Количество пассажиров
                </div>
                <div className="flex gap-3 flex-col [&>div:not(:last-child)]:border-b [&>div:not(:last-child)]:pb-3 [&>div:not(:last-child)]:border-zinc-100">
                    {AvailablePassengerTypes.map((type) =>
                        <PassengerCountSelect 
                            key={type}
                            title={PassengerTypeNames[type].title}
                            description={PassengerTypeNames[type].description}
                            count={passengers?.[type]?.count ?? 0}
                            setCount={(count) => handlePassengerCountChange(type, count)}
                        />
                    )}
                </div>
            </div>
            <div className="p-5 flex flex-col">
                <div className="mb-4 font-semibold text-lg">
                    Класс обслуживания
                </div>  
                <div className="flex gap-3 flex-col [&>div:not(:last-child)]:border-b [&>div:not(:last-child)]:pb-3 [&>div:not(:last-child)]:border-zinc-100">
                    {AvailableClassOfService.map((cos) =>
                        <ClassOfServiceSelectOption 
                            key={cos}
                            selected={classOfService}
                            option={cos}
                            setSelected={(c) => methods.setValue('classOfService', c, { shouldValidate: true })}
                            title={ClassOfServiceNames[cos].title}
                        />
                    )}
                </div>
            </div>
        </div>
    )
})